import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Button,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Switch,
    Tooltip,
    IconButton,
    TextField,
    Divider,
    Chip,
    Stack,
    CircularProgress
} from '@mui/material';

// Ikony
import InfoIcon from '@mui/icons-material/Info';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import SupportIcon from '@mui/icons-material/Support';
import BuildIcon from '@mui/icons-material/Build';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CalculateIcon from '@mui/icons-material/Calculate';
import SaveIcon from '@mui/icons-material/Save';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

// Stałe
const API_URL = process.env.REACT_APP_API_URL || '';

const EXCHANGE_RATES = {
    PLN: 1,
    EUR: 0.22,
    USD: 0.25,
    GBP: 0.19
};

const PRIORITY_MODIFIERS = {
    standard: 1,
    express: 1.2,
    urgent: 1.5
};

const VALID_DISCOUNT_CODES = ['SPECIAL2024', 'WELCOME10', 'PROMO25'];

export default function ProductCalculator() {
    // Stany podstawowe
    const [calculators, setCalculators] = useState([]);
    const [selectedCalculator, setSelectedCalculator] = useState('');
    const [components, setComponents] = useState([]);
    const [pricingRules, setPricingRules] = useState([]);
    const [selectedComponents, setSelectedComponents] = useState({});
    const [selectedModules, setSelectedModules] = useState({});
    const [customValues, setCustomValues] = useState({});
    const [finalPrice, setFinalPrice] = useState(0);
    const [currency, setCurrency] = useState('PLN');
    const [isAnnualPayment, setIsAnnualPayment] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [isLoading, setIsLoading] = useState(true);

    // Stany zaawansowane
    const [selectedPriority, setSelectedPriority] = useState('standard');
    const [additionalSupport, setAdditionalSupport] = useState(false);
    const [customization, setCustomization] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [subscriptionLength, setSubscriptionLength] = useState(1);
    const [discountCode, setDiscountCode] = useState('');
    const [isDiscountValid, setIsDiscountValid] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Nowy stan dla SLA
    const [selectedSLA, setSelectedSLA] = useState('basic'); // 'basic', 'standard', 'premium'

    // Pobranie kalkulatorów przy montowaniu komponentu
    useEffect(() => {
        fetchCalculators();
    }, []);

    const fetchCalculators = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${API_URL}/api/calculators`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setCalculators(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching calculators:', error);
            showAlert('Błąd podczas pobierania kalkulatorów', 'error');
            setCalculators([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchComponents = async (calculatorId) => {
        try {
            const response = await fetch(`${API_URL}/api/calculators/${calculatorId}/components`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setComponents(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching components:', error);
            showAlert('Błąd podczas pobierania komponentów', 'error');
            setComponents([]);
        }
    };

    const fetchPricingRules = async (calculatorId) => {
        try {
            const response = await fetch(`${API_URL}/api/calculators/${calculatorId}/pricing-rules`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setPricingRules(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching pricing rules:', error);
            showAlert('Błąd podczas pobierania reguł cenowych', 'error');
            setPricingRules([]);
        }
    };

    const handleCalculatorChange = async (calculatorId) => {
        setSelectedCalculator(calculatorId);
        resetSelections();
        await fetchComponents(calculatorId);
        await fetchPricingRules(calculatorId);
    };

    const resetSelections = () => {
        setSelectedComponents({});
        setSelectedModules({});
        setCustomValues({});
        setFinalPrice(0);
        setQuantity(1);
        setSubscriptionLength(1);
        setSelectedPriority('standard');
        setAdditionalSupport(false);
        setCustomization(false);
        setDiscountCode('');
        setIsDiscountValid(false);
        setSelectedSLA('basic'); // reset SLA
    };

    const validateDiscountCode = (code) => {
        return VALID_DISCOUNT_CODES.includes(code);
    };

    const handleDiscountCodeChange = (event) => {
        const code = event.target.value.toUpperCase();
        setDiscountCode(code);
        setIsDiscountValid(validateDiscountCode(code));
    };

    const getDiscountPercentage = (code) => {
        switch (code) {
            case 'SPECIAL2024':
                return 5;
            case 'WELCOME10':
                return 10;
            case 'PROMO25':
                return 25;
            default:
                return 0;
        }
    };

    const showAlert = (message, severity) => {
        setAlert({ open: true, message, severity });
    };

    const calculatePrice = () => {
        const calculator = calculators.find(calc => calc.id === selectedCalculator);
        if (!calculator) return;

        let price = 0;

        // ------------------
        // 1) PRICEMONIT
        // ------------------
        if (calculator.company === 'pricemonit') {
            // Pobierz regułę bazową z pricingRules (np. 'base_rate')
            const baseRule = pricingRules.find(
                rule => rule.element_key === 'base_rate' && rule.currency === currency
            );
            price = baseRule ? baseRule.cost : calculator.base_price;

            // Jeśli waluta różna od bazowej, sprawdź dedykowaną regułę lub przelicznik
            if (currency !== calculator.base_currency) {
                const currencyRule = pricingRules.find(
                    rule => rule.element_key === `currency_specific_${currency.toLowerCase()}`
                );
                if (currencyRule) {
                    price = currencyRule.cost;
                } else {
                    // W razie braku reguły, używamy EXCHANGE_RATES
                    price = calculator.base_price * (1 / EXCHANGE_RATES[currency]);
                }
            }

            // Uwzględnienie ilości
            price *= quantity;

            // Uwzględnienie subskrypcji (lub płatności rocznej)
            if (isAnnualPayment) {
                price = price * 12 * 0.9;
            } else {
                price *= subscriptionLength;
            }

            // ------------------
            // 2) AITEAMS
            // ------------------
        } else if (calculator.company === 'aiteams') {
            // Sumujemy koszt wszystkich zaznaczonych modułów (z pricingRules)
            pricingRules
                .filter(rule => rule.element_key.startsWith('module_'))
                .forEach(rule => {
                    if (selectedModules[rule.element_key]) {
                        price += rule.cost;
                    }
                });

            // Ilość i subskrypcja
            price *= quantity;
            price *= subscriptionLength;

            // Obsługa SLA
            if (selectedSLA === 'standard') {
                price += 500; // +15%
            } else if (selectedSLA === 'premium') {
                price += 1000; // +30%
            }
        }

        // Globalne modyfikatory
        price *= PRIORITY_MODIFIERS[selectedPriority];
        if (additionalSupport) price *= 1.15;
        if (customization) price *= 1.25;

        // Kod rabatowy
        if (isDiscountValid) {
            const discountPercent = getDiscountPercentage(discountCode);
            price *= (1 - discountPercent / 100);
        }

        setFinalPrice(Math.round(price * 100) / 100);
    };

    const handleSaveCalculation = async () => {
        try {
            setIsSaving(true);
            const response = await fetch(`${API_URL}/api/calculations`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    calculator_id: selectedCalculator,
                    final_price: finalPrice,
                    components_used: {
                        selected: selectedComponents,
                        custom: customValues,
                        priority: selectedPriority,
                        additionalSupport,
                        customization,
                        quantity,
                        subscriptionLength,
                        modules: selectedModules,
                        sla: selectedSLA // zapisujemy wybrany poziom SLA
                    },
                    currency,
                    is_annual: isAnnualPayment,
                    discount_code: isDiscountValid ? discountCode : null
                })
            });
            if (!response.ok) {
                throw new Error('Failed to save calculation');
            }
            showAlert('Kalkulacja została zapisana', 'success');
        } catch (error) {
            console.error('Error saving calculation:', error);
            showAlert('Błąd podczas zapisywania kalkulacji', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h4" gutterBottom>
                        Kalkulator wyceny usług
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Wybór kalkulatora */}
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Wybierz kalkulator</InputLabel>
                                <Select
                                    value={selectedCalculator}
                                    onChange={(e) => handleCalculatorChange(e.target.value)}
                                    label="Wybierz kalkulator"
                                >
                                    {calculators.map(calc => (
                                        <MenuItem key={calc.id} value={calc.id}>
                                            {calc.name} ({calc.base_price} {calc.base_currency})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Waluta</InputLabel>
                                <Select
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    label="Waluta"
                                >
                                    <MenuItem value="PLN">PLN</MenuItem>
                                    <MenuItem value="EUR">EUR</MenuItem>
                                    <MenuItem value="USD">USD</MenuItem>
                                    <MenuItem value="GBP">GBP</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {selectedCalculator && (
                            <>
                                {/* Opcje zaawansowane */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Opcje zaawansowane" color="primary" />
                                    </Divider>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Card>
                                        <CardContent>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <PriorityHighIcon />
                                                <FormControl fullWidth>
                                                    <InputLabel>Priorytet</InputLabel>
                                                    <Select
                                                        value={selectedPriority}
                                                        onChange={(e) => setSelectedPriority(e.target.value)}
                                                        label="Priorytet"
                                                    >
                                                        <MenuItem value="standard">Standard</MenuItem>
                                                        <MenuItem value="express">Express (+20%)</MenuItem>
                                                        <MenuItem value="urgent">Urgent (+50%)</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Card>
                                        <CardContent>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <SupportIcon />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={additionalSupport}
                                                            onChange={(e) => setAdditionalSupport(e.target.checked)}
                                                        />
                                                    }
                                                    label="Wsparcie dodatkowe (+15%)"
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Card>
                                        <CardContent>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <BuildIcon />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={customization}
                                                            onChange={(e) => setCustomization(e.target.checked)}
                                                        />
                                                    }
                                                    label="Customizacja (+25%)"
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Komponenty (dla Pricemonit lub cokolwiek w tabeli calculator_components) */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Komponenty" color="primary" />
                                    </Divider>
                                </Grid>

                                {components.map(component => (
                                    <Grid item xs={12} sm={6} md={4} key={component.id}>
                                        <Card>
                                            <CardContent>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={selectedComponents[component.id] || component.is_required}
                                                            onChange={(e) => {
                                                                if (!component.is_required) {
                                                                    setSelectedComponents(prev => ({
                                                                        ...prev,
                                                                        [component.id]: e.target.checked
                                                                    }));
                                                                }
                                                            }}
                                                            disabled={component.is_required}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            {component.name}
                                                            <Tooltip title={`Typ: ${component.component_type}, Modyfikator: ${component.price_modifier}`}>
                                                                <IconButton size="small">
                                                                    <InfoIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    }
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}

                                {/* Dla AITeams: wyświetlamy moduły dynamicznie z pricingRules */}
                                {calculators.find(calc => calc.id === selectedCalculator)?.company === 'aiteams' && (
                                    <>
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 2 }}>
                                                <Chip label="Moduły AITeams" color="primary" />
                                            </Divider>
                                        </Grid>

                                        {pricingRules
                                            .filter(rule => rule.element_key.startsWith('module_'))
                                            .map(rule => (
                                                <Grid item xs={12} md={4} key={rule.id}>
                                                    <Card>
                                                        <CardContent>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={!!selectedModules[rule.element_key]}
                                                                        onChange={(e) => {
                                                                            setSelectedModules(prev => ({
                                                                                ...prev,
                                                                                [rule.element_key]: e.target.checked
                                                                            }));
                                                                        }}
                                                                    />
                                                                }
                                                                label={rule.description || rule.element_key}
                                                            />
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}

                                        {/* Dropdown SLA */}
                                        <Grid item xs={12} md={6}>
                                            <Card>
                                                <CardContent>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Poziom SLA</InputLabel>
                                                        <Select
                                                            value={selectedSLA}
                                                            onChange={(e) => setSelectedSLA(e.target.value)}
                                                            label="Poziom SLA"
                                                        >
                                                            <MenuItem value="basic">Basic (0%)</MenuItem>
                                                            <MenuItem value="standard">Standard (+15%)</MenuItem>
                                                            <MenuItem value="premium">Premium (+30%)</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </>
                                )}

                                {/* Opcje zamówienia */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Opcje zamówienia" color="primary" />
                                    </Divider>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        type="number"
                                        label="Ilość"
                                        value={quantity}
                                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                        fullWidth
                                        InputProps={{ inputProps: { min: 1 } }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Długość subskrypcji</InputLabel>
                                        <Select
                                            value={subscriptionLength}
                                            onChange={(e) => setSubscriptionLength(e.target.value)}
                                            label="Długość subskrypcji"
                                        >
                                            {[1, 3, 6, 12].map(months => (
                                                <MenuItem key={months} value={months}>
                                                    {months} {months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <LocalOfferIcon />
                                                <TextField
                                                    fullWidth
                                                    label="Kod rabatowy"
                                                    value={discountCode}
                                                    onChange={handleDiscountCodeChange}
                                                    error={discountCode !== '' && !isDiscountValid}
                                                    helperText={discountCode !== '' && !isDiscountValid ? 'Nieprawidłowy kod' : ''}
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={isAnnualPayment}
                                                        onChange={(e) => setIsAnnualPayment(e.target.checked)}
                                                    />
                                                }
                                                label="Płatność roczna (10% zniżki)"
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Przycisk kalkulacji i zapisu */}
                                <Grid item xs={12}>
                                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={calculatePrice}
                                            startIcon={<CalculateIcon />}
                                        >
                                            Oblicz cenę
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            onClick={handleSaveCalculation}
                                            disabled={!finalPrice || isSaving}
                                            startIcon={<SaveIcon />}
                                        >
                                            {isSaving ? 'Zapisywanie...' : 'Zapisz kalkulację'}
                                        </Button>
                                    </Box>
                                </Grid>

                                {finalPrice > 0 && (
                                    <Grid item xs={12}>
                                        <Card sx={{ mt: 2, bgcolor: 'primary.light' }}>
                                            <CardContent>
                                                <Typography variant="h5" color="white" align="center">
                                                    Finalna cena od: {finalPrice.toLocaleString()} {currency}
                                                    {isAnnualPayment && ' (rocznie)'}
                                                </Typography>
                                                {isDiscountValid && (
                                                    <Typography color="white" align="center" variant="subtitle1">
                                                        Uwzględniono rabat: {getDiscountPercentage(discountCode)}%
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </Paper>
            </Box>

            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}